import { defineStore } from 'pinia';
import { defineAsyncComponent } from 'vue';

// Import SVG assets
import schedulerIconUrl from '../assets/scheduler.svg';
import managerIconUrl from '../assets/manager.svg';

// Import loading and error components for async components
import AppLoading from '../components/AppLoading.vue';
import AppError from '../components/AppError.vue';

export interface AppDefinition {
  id: string;
  name: string;
  iconUrl: string;
  component: ReturnType<typeof defineAsyncComponent>; // Type for async component definition
}

interface AppState {
  availableApps: AppDefinition[];
  currentAppId: string | null;
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    availableApps: [
      {
        id: 'productionScheduler',
        name: 'Production Scheduler',
        iconUrl: schedulerIconUrl,
        component: defineAsyncComponent({
          loader: () => import('../modules/production-scheduler/components/ProductionSchedulerApp.vue'),
          loadingComponent: AppLoading,
          errorComponent: AppError,
          delay: 200, // Optional: delay before showing loading component
          timeout: 10000, // Optional: time before showing error component
        })
      },
      {
        id: 'productionManager',
        name: 'Production Manager',
        iconUrl: managerIconUrl,
        component: defineAsyncComponent({
          loader: () => import('../modules/production-manager/components/ProductionManagerApp.vue'),
          loadingComponent: AppLoading,
          errorComponent: AppError,
          delay: 200,
          timeout: 10000,
        })
      }
    ],
    currentAppId: 'productionScheduler',
  }),
  getters: {
    currentApp(state): AppDefinition | undefined {
      return state.availableApps.find(app => app.id === state.currentAppId);
    },
    // The currentAppComponent getter now directly returns the async component definition
    currentAppComponent(_state): ReturnType<typeof defineAsyncComponent> | null {
      const app = this.currentApp;
      return app ? app.component : null;
    }
  },
  actions: {
    setCurrentApp(appId: string) {
      const appExists = this.availableApps.some(app => app.id === appId);
      if (appExists) {
        this.currentAppId = appId;
      } else {
        console.warn(`App with id "${appId}" not found.`);
      }
    },
  },
}); 